import { useDebouncedCallback, useIntersection } from '@mantine/hooks';
import { Box, LinearProgress, Theme, Typography } from '@mui/material';
import { BoxProps, SxProps, SystemStyleObject } from '@mui/system';
import { useTheme } from '@mui/material';
import { Fragment, useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { bloxValues } from 'themes/constants';

export type IntersectionLoaderProps = {
  hasNextPage?: ReturnType<typeof useInfiniteQuery>['hasNextPage'];
  isFetching?: ReturnType<typeof useInfiniteQuery>['isFetching'];
  fetchNextPage: ReturnType<typeof useInfiniteQuery>['fetchNextPage'];
  ixRef: ReturnType<typeof useIntersection>['ref'];
  entry: ReturnType<typeof useIntersection>['entry'];
  endOfResultsMessage?: string; // String or empty/null to autohide
  sx?: SxProps<Theme>; // Custom sx to the wrapper, can target IDs if needed
  compact?: boolean; // Compact mode for smaller height on loader/end of results
  polymorph?: BoxProps['component']; // Polymorph as another component
  hideOnPagesEnd?: boolean; // Return null jsx when on pagination end
};

export const getLoaderSx = (
  theme: Theme,
  compact?: boolean,
  noMessage?: boolean,
): SystemStyleObject => {
  // give some height to the loader even if hiding msg
  // so that the intersection observer can still trigger
  const varHeight = noMessage ? '2px' : compact ? '24px' : '40px';
  const varMinHeight = noMessage ? '2px' : compact ? '24px' : '40px';
  const varMaxHeight = noMessage ? '2px' : compact ? '24px' : '40px';
  const varMy = noMessage ? '2px' : compact ? 0.5 : 1;
  const varBg = noMessage ? 'transparent' : theme.palette.action.hover;
  const varBorder = noMessage ? 'none' : `1px solid ${theme.palette.action.hover}`;

  const result = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: varHeight,
    minHeight: varMinHeight,
    maxHeight: varMaxHeight,
    my: varMy,
    borderRadius: 1,
    bgcolor: varBg,
    border: varBorder,
    '& #IntersectionLoaderTypography': {
      fontSize: compact ? bloxValues.smallFontSizeRem : bloxValues.regularFontSizeRem,
      color: theme.palette.text.disabled,
    },
    '& #IntersectionLoaderLinearProgress': {
      width: '100%',
      bgcolor: 'transparent',
    },
  };

  return result;
};

export const IntersectionLoader = ({
  hasNextPage,
  isFetching,
  fetchNextPage,
  ixRef,
  entry,
  endOfResultsMessage,
  sx,
  compact = false,
  polymorph,
  hideOnPagesEnd = false,
}: IntersectionLoaderProps) => {
  const theme = useTheme();

  // Debounce the fetchNextPage function, help prevent any double fetches
  const debouncedFetchNextPage = useDebouncedCallback(fetchNextPage, 300);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetching) {
      debouncedFetchNextPage();
    }
  }, [entry, hasNextPage, isFetching, debouncedFetchNextPage]);

  // end of results msg hidden if not provided
  const hideMessage = !Boolean(endOfResultsMessage);

  const mergedSx = useMemo(() => {
    return {
      ...getLoaderSx(theme, compact, hideMessage),
      ...(typeof sx === 'function' ? sx(theme) : sx),
    };
  }, [theme, compact, hideMessage, sx]);

  // null if hide prop set and no more pages to fetch
  const hideOnEnd = hideOnPagesEnd && !hasNextPage && !isFetching;

  if (hideOnEnd) {
    return null;
  }

  return (
    <Box
      id="IntersectionLoader"
      ref={ixRef}
      sx={mergedSx}
      component={polymorph}
    >
      {isFetching ? (
        <LinearProgress
          title="Loading..."
          id="IntersectionLoaderLinearProgress"
        />
      ) : !hasNextPage ? (
        <Fragment>
          {hideMessage ? null : (
            <Typography id="IntersectionLoaderTypography">
              {endOfResultsMessage}
            </Typography>
          )}
        </Fragment>
      ) : null}
    </Box>
  );
};
